/**
 * The mutation primitive. Every write in this package goes through it.
 *
 * The problem it solves is narrow and easy to get wrong: TanStack Query retries a failed
 * mutation by calling `mutationFn` again with **the same variables object**. So if the key
 * is minted inside `mutationFn`, every retry gets a fresh key and the server treats it as
 * a new logical operation — which is precisely how a retried goods receipt posts stock
 * twice. Minting it in the wrapper, and carrying it *in* the variables, makes the retry
 * reuse it automatically and makes a second click a genuinely different operation.
 *
 *     const receive = useReceiveStock();
 *     receive.mutate(payload);     // one key, reused across every retry of this attempt
 *     receive.mutate(payload);     // a second click is a second operation, second key
 *
 * For an offline queue, `mutateWithKey` adopts the client UUID the queue entry was created
 * with (PROJECT.md §8), so a write that drains hours later from a different session keeps
 * its identity. In remote mode the idempotency record lives in the canonical service
 * (D-37), so it is deduplicated even if the retry lands on a different host application.
 */
import { useMutation } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';
import { isRetryable } from '../http/errors.js';
import { beginOperation, operationFromKey, } from '../http/idempotency.js';
export function useIdempotentMutation(options) {
    const { mutationFn, maxRetries = 2, ...rest } = options;
    const lastKey = useRef(null);
    const inner = useMutation({
        ...rest,
        mutationFn: (variables) => mutationFn(variables.input, variables.operation),
        retry: (failureCount, error) => failureCount < maxRetries && isRetryable(error),
    });
    const start = useCallback((input, operation) => {
        lastKey.current = operation.key;
        return { input, operation };
    }, []);
    const mutate = useCallback((input) => {
        inner.mutate(start(input, beginOperation()));
    }, [inner, start]);
    const mutateAsync = useCallback((input) => inner.mutateAsync(start(input, beginOperation())), [inner, start]);
    const mutateWithKey = useCallback((input, key) => inner.mutateAsync(start(input, operationFromKey(key))), [inner, start]);
    return {
        mutate,
        mutateAsync,
        mutateWithKey,
        currentKey: lastKey.current,
        data: inner.data,
        error: inner.error,
        isPending: inner.isPending,
        isError: inner.isError,
        isSuccess: inner.isSuccess,
        reset: inner.reset,
    };
}
//# sourceMappingURL=useIdempotentMutation.js.map